import { useTranslation } from "react-i18next";

import type { Step } from "./step.js";

/**
 * The one control at the bottom of the screen.
 *
 * ⚠️ **One, deliberately.** Phase 14 B2: two buttons of equal weight is how a
 * player ends up locking an empty guess — so the lock is disabled until there is
 * something to lock, and a stage whose action is *positional* gets a sentence
 * rather than a button that cannot express it.
 */
export function PrimaryAction({
  step,
  busy,
  canLock,
  onDeal,
  onLock,
  onPass,
  onDefend,
  onAllow,
  canDefend,
  onRematch,
  gameId,
}: {
  step: Step;
  busy: boolean;
  canLock: boolean;
  onDeal: () => void;
  onLock: () => void;
  onPass: () => void;
  onDefend: () => void;
  onAllow: () => void;
  /** Whether the block is affordable — the engine checks again regardless. */
  canDefend: boolean;
  onRematch: () => void;
  gameId: string | null;
}) {
  const { t } = useTranslation();

  const waiting = (text: string) => (
    <p className="flex-1 text-sm text-white/45">{text}</p>
  );
  const button = (label: string, onClick: () => void, disabled = false) => (
    <button
      type="button"
      disabled={busy || disabled}
      onClick={onClick}
      className="min-h-12 flex-1 rounded-lg bg-flash-500 text-base font-semibold text-stage-950 disabled:opacity-40"
    >
      {label}
    </button>
  );

  switch (step) {
    case "deal":
      return button(t("round.deal"), onDeal);
    case "answer":
      return button(t("round.lockIn"), onLock, !canLock);
    case "challenge":
      // Aiming happens on the timeline in the stage; **declining** is the one
      // button the phase has, and it is what closes the window early for a room
      // that has made up its mind (16b's ready check).
      return button(t("round.challenge.pass"), onPass);
    case "advanceRound":
      return button(t("round.nextRound"), onDeal);
    case "defend":
      // The one control the moment has. Declining is what the clock does anyway,
      // so it is the quiet secondary — a second button of equal weight here
      // would be asking somebody to make a decision twice under a timer.
      return (
        <>
          {button(t("round.defend.pay"), onDefend, !canDefend)}
          <button
            type="button"
            disabled={busy}
            onClick={onAllow}
            className="min-h-12 shrink-0 rounded-lg border border-white/20 px-3 text-sm font-medium disabled:opacity-40"
          >
            {t("round.defend.allow")}
          </button>
        </>
      );
    case "stealPending":
      return waiting(t("round.defend.waiting"));
    case "stallWaiting":
      return waiting(t("stall.waitingAction"));
    case "stallVote":
      // Positional, like `place`: **two answers of equal weight**, and B2 says
      // the bar holds one action or none. They live on the notice in the stage,
      // where the tally that makes them meaningful is already on screen.
      return waiting(t("stall.voteAction"));
    case "place":
      // Positional: the action is tapping a gap on the timeline in the stage,
      // and a button here would either duplicate it or sit beside it with equal
      // weight — which is the mistake B2 named.
      return waiting(t("round.placeHere"));
    case "answered":
      return waiting(t("round.lockedIn"));
    case "finished":
      return (
        <>
          {button(t("round.rematch"), onRematch)}
          {gameId && (
            <a
              href={`/recap/${gameId}`}
              className="min-h-12 shrink-0 rounded-lg border border-white/20 px-4 text-sm font-medium leading-[3rem]"
            >
              {t("round.seeRecap")}
            </a>
          )}
        </>
      );
    case "waitDeal":
    case "listen":
    case "watchPlace":
    case "awaitChallenge":
    case "reveal":
      return waiting(t("round.waitingFor"));
  }
}
